"use client";

import { createContext, useMemo, useReducer, useEffect, useState, useContext, useRef } from "react";
import cartApi from "utils/__api__/cart";
import { AuthContext } from "contexts/AuthContext";

const INITIAL_CART = [];
const INITIAL_STATE = {
  cart: INITIAL_CART,
  note: "",
  coupon: "",
  sessionId: null,
};


const reducer = (state, action) => {
  switch (action.type) {
    case "SET_NOTE": {
      const newState = { ...state, note: action.payload };
      window.localStorage.setItem("d2gcart", JSON.stringify(newState));
      return newState;
    }

    case "SET_COUPON": {
      const newState = { ...state, coupon: action.payload };
      window.localStorage.setItem("d2gcart", JSON.stringify(newState));
      return newState;
    }

    case "SET_SESSION": {
      const newState = { ...state, sessionId: action.payload };
      window.localStorage.setItem("d2gcart", JSON.stringify(newState));
      return newState;
    }

    case "CHANGE_CART_AMOUNT": {
      let cartList = [...state.cart];
      const cartItem = action.payload;
      const existIndex = cartList.findIndex((item) => item.id === cartItem.id);

      if (cartItem.qty === 0) {
        const updatedCart = cartList.filter((item) => item.id !== cartItem.id);
        const newState = { ...state, cart: updatedCart };
        window.localStorage.setItem("d2gcart", JSON.stringify(newState));
        return newState;
      }

      if (cartItem.qty < 1) {
        if (existIndex > -1 && cartList[existIndex].qty - 1 <= 0) {
          const updatedCart = cartList.filter((item) => item.id !== cartItem.id);
          const newState = { ...state, cart: updatedCart };
          window.localStorage.setItem("d2gcart", JSON.stringify(newState));
          return newState;
        } else if (existIndex > -1) {
          cartList[existIndex] = {
            ...cartList[existIndex],
            qty: cartList[existIndex].qty - 1,
          };
          const newState = { ...state, cart: cartList };
          window.localStorage.setItem("d2gcart", JSON.stringify(newState));
          return newState;
        }
      }

      if (existIndex > -1) {
        cartList[existIndex] = {
          ...cartList[existIndex],
          qty: cartList[existIndex].qty + (cartItem.qty || 1),
        };
      } else {
        cartList.push({ ...cartItem, qty: cartItem.qty || 1 });
      }

      const newState = { ...state, cart: cartList };
      window.localStorage.setItem("d2gcart", JSON.stringify(newState));
      return newState;
    }

    case "RESET_CART": {
      window.localStorage.removeItem("d2gcart");
      return INITIAL_STATE;
    }

    default:
      return state;
  }
};

const init = () => {
  if (typeof window !== "undefined") {
    try {
      const stored = JSON.parse(window.localStorage.getItem("d2gcart"));
      return {
        cart: stored?.cart || [],
        note: stored?.note || "",
        coupon: stored?.coupon || "",
        sessionId: stored?.sessionId || null,
      };
    } catch (e) {
      console.error("Failed to parse cart from window.localStorage:", e);
    }
  }
  return INITIAL_STATE;
};


export const CartContext = createContext();

export default function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE, init);
  const [hasMounted, setHasMounted] = useState(false);
  const hasRun = useRef(false);
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated === null) return;

    async function ensureSession() {
      try {
        if (auth.isAuthenticated === false) {
          dispatch({ type: "SET_SESSION", payload: null });
          return;
        }

        if (auth.isAuthenticated && !state.sessionId) {
          const newSession = await cartApi.createCartSessionClient();

          if (state.cart.length > 0) {
            await syncCart(newSession.data.id, state.cart);
          }

          dispatch({ type: "SET_SESSION", payload: newSession.data.id });

          const cartItems = await cartApi.getCartSessionByIdClient(newSession.data.id);

          const items = cartItems.data.data[0]?.carts || [];

          const mapped = items
            .filter((item) => item?.product && item?.product?.id)
            .map((item) => ({
              id: item.product.id,
              name: item.product.name || "",
              photo: item.product.photo || "",
              price: item.product.discount_price > 0 ? item.product.discount_price : item.product.price || 0,
              qty: item.quantity || 1,
              vendor: item.product.vendor || "",
            }));

          await resetCart();
          for (const sessionItem of mapped) { addToCart(sessionItem, sessionItem.qty); }
          const newState = { ...state, cart: mapped, sessionId: newSession.data.id };
          window.localStorage.setItem("d2gcart", JSON.stringify(newState));
          dispatch({ type: "SET_SESSION", payload: newSession.data.id });
        }
      } catch (err) {
        console.error("❌ Error al crear o sincronizar sesión:", err);
      }
    }

    ensureSession();
  }, [auth.isAuthenticated]);


  const setNote = (note) => dispatch({ type: "SET_NOTE", payload: note });
  const setCoupon = (coupon) => dispatch({ type: "SET_COUPON", payload: coupon });
  const resetCart = () => dispatch({ type: "RESET_CART" });

  const createCart = async () => {
    try {

      if (!state.sessionId) {
        const newSession = await cartApi.createCartSessionClient();
        dispatch({ type: "SET_SESSION", payload: newSession.data.id });
      }
    } catch (error) {
      console.error("Error al crear el carrito:", error);
    }
  };

  const addToCart = async (product, quantity) => {
    try {

      if (state.sessionId) {
        await cartApi.addProductToSessionClient(state.sessionId, {
          product_id: product.id,
          price: product.price,
          quantity: quantity,
        });
      }
      dispatch({
        type: "CHANGE_CART_AMOUNT",
        payload: { ...product, qty: quantity },
      });
    } catch (error) {
      console.error("Error al agregar producto al carrito:", error);
    }
  };

  const decreaseFromCart = async (product) => {
    try {

      if (state.sessionId) {
        await cartApi.updateProductInSessionClient(product.cart_id, {
          product_id: product.id,
          quantity: product.qty - 1,
          price: product.price,
        });
      }
      dispatch({ type: "CHANGE_CART_AMOUNT", payload: { ...product, qty: -1 } });

    } catch (error) {
      console.error("Error al actualizar producto:", error);
    }
  };

  const removeFromCart = async (product) => {
    try {


      if (state.sessionId) {
        await cartApi.deleteProductFromSessionClient(product.id);
      }         
      dispatch({ type: "CHANGE_CART_AMOUNT", payload: { ...product, qty: 0 } });

    } catch (error) {
      console.error("Error al eliminar producto:", error);
    }
  };
  const syncCart = async (cartId, cartInformation) => {
    try {
      if (cartInformation) {
        await cartApi.syncProductInSessionClient(cartId, cartInformation);
      }
    } catch (error) {
      console.error("Error al sincronizar carrito:", error);
    }
  };

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      setCoupon,
      setNote,
      resetCart,
      addToCart,
      decreaseFromCart,
      removeFromCart,
      syncCart,
      createCart
    }),
    [state]
  );

  if (!hasMounted) return null;

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}
